/**
 * The getting-started checklist: what a new user has done, and what is left.
 *
 * A checklist rather than a longer tour, because the two are not equivalent.
 * Published benchmarks put a tour launched from a checklist near 67% completion
 * against roughly 23% for a standalone auto-triggered one, and user-triggered
 * tours ahead of automatic ones by two to three times. Exploring is also not
 * linear — somebody who wants to try an export before running an optimization
 * should not have to sit through four popovers to get there.
 *
 * State is per-browser, in localStorage, for the same reason the tour flag is:
 * the users table was created by hand on production and does not receive
 * migrations, so persisting this properly means a hand-run DDL change against a
 * database with no automated backups. A checklist reappearing on a second
 * machine is a tolerable failure; a broken users table is not.
 *
 * Every read is defensive. Private browsing throws on localStorage access, and
 * a checklist is never worth taking the app down for.
 */

import { computed, ref } from 'vue'

const STORAGE_KEY = 'scvc_getting_started_v1'
const DISMISSED_KEY = 'scvc_getting_started_dismissed_v1'

/**
 * Ordered so the first item teaches and the rest can be done in any order.
 *
 * `action` names what the host view should do when the row is clicked. Items
 * without one are completed by using the product, not by the checklist.
 */
export const CHECKLIST_ITEMS = [
  {
    id: 'core_tour',
    label: 'Take the four-step tour',
    hint: 'The map, the cost row, and how to run an optimization',
    icon: 'mdi-compass-outline',
    action: 'start-core-tour',
  },
  {
    id: 'run_optimization',
    label: 'Run your first optimization',
    hint: 'Watch the cost row change once it finishes',
    icon: 'mdi-rocket-launch-outline',
    action: 'run-optimization',
  },
  {
    id: 'open_stat_card',
    label: 'Open a summary stat card',
    hint: 'Every card opens a full breakdown',
    icon: 'mdi-view-dashboard-outline',
  },
  {
    id: 'discover_tour',
    label: 'See where everything lives',
    hint: 'Configuration, imports and reports, in one pass',
    icon: 'mdi-map-search-outline',
    action: 'start-discover-tour',
  },
  {
    id: 'export_report',
    label: 'Export a report',
    hint: 'PowerPoint for the deck, Excel for the numbers',
    icon: 'mdi-file-powerpoint-outline',
    action: 'open-reporting',
  },
]

// Module-level so every caller shares one set of ticks. A per-instance ref
// would let the banner and the menu disagree about what had been done.
const completed = ref(readCompleted())
const dismissed = ref(readDismissed())

function readCompleted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function readDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completed.value))
  } catch {
    // The checklist reverts to unticked next visit. Not worth handling.
  }
}

export function useGettingStarted() {
  const isDone = (id) => completed.value.includes(id)

  /**
   * Tick an item off. Safe to call on every optimization, not just the first.
   *
   * Callers are ordinary product code paths — the run-optimization handler, the
   * stat card click — so this has to be idempotent and cheap. It writes only
   * when something actually changed.
   */
  function markDone(id) {
    if (!CHECKLIST_ITEMS.some((item) => item.id === id)) return
    if (completed.value.includes(id)) return
    completed.value = [...completed.value, id]
    persist()
  }

  function dismiss() {
    dismissed.value = true
    try {
      localStorage.setItem(DISMISSED_KEY, 'true')
    } catch { /* see above */ }
  }

  function reset() {
    completed.value = []
    dismissed.value = false
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(DISMISSED_KEY)
    } catch { /* see above */ }
  }

  const doneCount = computed(
    () => CHECKLIST_ITEMS.filter((item) => completed.value.includes(item.id)).length,
  )
  const total = CHECKLIST_ITEMS.length
  const allDone = computed(() => doneCount.value >= total)

  // Hidden once finished as well as once dismissed. A checklist with every box
  // ticked is clutter, and leaving it up implies there is still something to do.
  const shouldShow = computed(() => !dismissed.value && !allDone.value)

  return {
    items: CHECKLIST_ITEMS,
    completed,
    dismissed,
    isDone,
    markDone,
    dismiss,
    reset,
    doneCount,
    total,
    allDone,
    shouldShow,
  }
}
